/**
 * 단발성 비동기 데이터 로드를 표준화하는 경량 훅.
 *
 * React Query 가 없으므로 useState/useEffect 기반으로 "fetcher 호출 → 로딩/에러/데이터 상태"
 * 보일러플레이트를 한 곳으로 모은다. `useGraphAtAsOf` 와 동일한 반환 형태(UseAsyncDataResult)를
 * SSOT 로 공유한다 — 단, 이 훅은 debounce 가 없는 단순 케이스 전용이다(연속 조작 debounce 가
 * 필요하면 `useGraphAtAsOf` 같은 특화 훅을 쓴다).
 *
 * - `deps` identity 가 바뀌면 자동 재호출.
 * - cancelled 가드 내장 — unmount/deps 변경 시 늦게 도착한 응답이 상태를 덮어쓰지 않는다.
 * - `enabled=false` 면 fetch 보류(예: 인증 hydrate 대기). false → true 전환 시 즉시 호출.
 * - `refresh()` 로 동일 deps 에서 수동 재호출(예: 생성/삭제 후 목록 갱신).
 *
 * @template T fetcher 가 resolve 하는 데이터 타입
 * @param fetcher 데이터를 가져오는 async 함수. deps 변경/refresh 시마다 호출된다.
 * @param deps fetcher 재호출을 트리거하는 의존성 배열(useEffect 의존성과 동일 의미).
 * @param opts.enabled false 면 fetch 보류. 기본 true.
 */
import { useEffect, useState } from "react"

/** 비동기 데이터 로드 결과. `useGraphAtAsOf` 와 동형 시그니처. */
export type UseAsyncDataResult<T> = {
	readonly data: T | undefined
	readonly isLoading: boolean
	readonly error: string | undefined
	readonly refresh: () => void
}

export function useAsyncData<T>(
	fetcher: () => Promise<T>,
	deps: ReadonlyArray<unknown>,
	opts: { readonly enabled?: boolean } = {}
): UseAsyncDataResult<T> {
	const { enabled = true } = opts
	const [data, setData] = useState<T | undefined>()
	const [isLoading, setIsLoading] = useState(false)
	const [error, setError] = useState<string | undefined>()
	const [refreshSeq, setRefreshSeq] = useState(0)

	// biome-ignore lint/correctness/useExhaustiveDependencies: deps 는 호출부가 전달하는 가변 의존성 배열이고 refreshSeq 는 refresh() 재호출 트리거 플래그이므로 의도적으로 spread 포함
	useEffect(() => {
		if (!enabled) return
		let cancelled = false
		setIsLoading(true)
		setError(undefined)
		fetcher()
			.then((res) => {
				if (!cancelled) setData(res)
			})
			.catch((e: Error) => {
				if (!cancelled) setError(e.message)
			})
			.finally(() => {
				if (!cancelled) setIsLoading(false)
			})
		return () => {
			cancelled = true
		}
	}, [...deps, enabled, refreshSeq])

	return {
		data,
		isLoading,
		error,
		refresh: () => setRefreshSeq((n) => n + 1)
	}
}
